import React, { useState, useEffect, useRef, useCallback } from "react";
import { LinearProgress } from "@material-ui/core";
import MicIcon from "@mui/icons-material/Mic";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import { ErrorData } from "../../../helpers";

function VmsgRecorder(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordings, setRecordings] = useState();
    const [recordingsUrl, setRecordingsUrl] = useState();
    const [time, setTime] = useState(0);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const isRecordingRef = useRef(false);
    const stoppingRef = useRef(false);
    const mountedRef = useRef(true);

    const cleanupStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }, []);

    const requestAudioStream = useCallback(async () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        }

        const legacyGetUserMedia =
            navigator.getUserMedia ||
            navigator.webkitGetUserMedia ||
            navigator.mozGetUserMedia ||
            navigator.msGetUserMedia;

        if (!legacyGetUserMedia) {
            throw new Error("getUserMedia is not supported");
        }

        return new Promise((resolve, reject) => {
            legacyGetUserMedia.call(navigator, { audio: true, video: false }, resolve, reject);
        });
    }, []);

    const startRecording = useCallback(() => {
        if (isRecordingRef.current || stoppingRef.current) return;
        setIsLoading(true);
        requestAudioStream()
            .then((stream) => {
                const AudioContext = window.AudioContext || window.webkitAudioContext || false;
                if (!AudioContext) {
                    throw new Error("Web Audio API is not supported");
                }

                const context = new AudioContext();
                const source = context.createMediaStreamSource(stream);
                const recorder = new window.WebAudioRecorder(source, {
                    workerDir: "/mp3/",
                    numChannels: 2,
                });

                recorder.setEncoding("mp3");
                recorder.setOptions({
                    timeLimit: props.timeLimit,
                    ogg: { quality: 0.5 },
                    mp3: { bitRate: 160 },
                    encodeAfterRecord: true,
                });

                recorder.onComplete = (rec, blob) => {
                    if (!mountedRef.current) return;
                    setRecordings(blob);
                    setRecordingsUrl(URL.createObjectURL(blob));
                    cleanupStream();
                };

                streamRef.current = stream;
                recorderRef.current = recorder;
                recorder.startRecording();

                if (!mountedRef.current) return;
                isRecordingRef.current = true;
                setIsRecording(true);
                setTime(0);
            })
            .catch((error) => {
                console.error(error);
                if (error?.name === "NotAllowedError") {
                    ErrorData("اجازه دسترسی به میکروفن داده نشد.");
                    return;
                }
                if (error?.name === "NotFoundError") {
                    ErrorData("میکروفن در دسترس نیست.");
                    return;
                }
                ErrorData("امکان ضبط صدا در مرورگر شما وجود ندارد.");
            })
            .finally(() => {
                if (mountedRef.current) setIsLoading(false);
            });
    }, [cleanupStream, props.timeLimit, requestAudioStream]);

    const stopRecording = useCallback(async () => {
        if (!isRecordingRef.current || stoppingRef.current) return;
        stoppingRef.current = true;
        setIsLoading(true);
        try {
            const recorder = recorderRef.current;
            isRecordingRef.current = false;
            setIsRecording(false);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
            if (recorder) {
                recorder.finishRecording();
            } else {
                cleanupStream();
            }
        } catch (e) {
            console.error(e);
            if (mountedRef.current) {
                isRecordingRef.current = false;
                setIsRecording(false);
            }
            cleanupStream();
        } finally {
            stoppingRef.current = false;
            if (mountedRef.current) setIsLoading(false);
        }
    }, [cleanupStream]);

    const handleRecordButton = () => {
        if (isRecordingRef.current) {
            stopRecording();
            return;
        }
        startRecording();
    };

    const handelReset = () => {
        if (recordingsUrl) {
            URL.revokeObjectURL(recordingsUrl);
        }
        setRecordingsUrl();
        setRecordings();
        setTime(0);
        recorderRef.current = null;
    };

    const getTime = () => {
        const convertToTwoDigitNumber = (input) => {
            return input > 9 ? "" + input : "0" + input;
        };
        const hours = parseInt(time / 60, 10);
        const seconds = time % 60;
        return `${convertToTwoDigitNumber(hours)}:${convertToTwoDigitNumber(seconds)}`;
    };

    const handleClose = async () => {
        if (isRecordingRef.current) {
            await stopRecording();
        }
        props.onClose();
    };

    useEffect(() => {
        if (!isRecording) return undefined;
        const intervalId = setInterval(() => {
            setTime((prev) => prev + 1);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [isRecording]);

    useEffect(() => {
        if (time >= props.timeLimit) {
            stopRecording();
        }
    }, [time, props.timeLimit, stopRecording]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (recorderRef.current && isRecordingRef.current && !stoppingRef.current) {
                recorderRef.current.cancelRecording();
            }
            cleanupStream();
            if (recordingsUrl) {
                URL.revokeObjectURL(recordingsUrl);
            }
        };
    }, [cleanupStream, recordingsUrl]);

    return (
        <div
            style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}
        >
            <div className="dialog-header">
                <div className="dialog-header-title">
                    <div className="dialog-header-icon">
                        <MicIcon sx={{ fontSize: "30px", margin: "0px" }} />
                    </div>
                    ضبط فایل صوتی
                </div>
                <IconButton
                    onClick={handleClose}
                    color="error"
                    sx={{
                        aspectRatio: 1,
                        width: "25px",
                        height: "25px",
                        backgroundColor: "rgb(250, 156, 156)",
                    }}
                    aria-label="delete"
                >
                    <ClearIcon />
                </IconButton>
            </div>
            <div style={{ width: "100%", display: "flex", justifyContent: "center", minHeight: 70, alignItems: "center" }}>
                {recordingsUrl ? (
                    <audio src={recordingsUrl} controls />
                ) : !isRecording ? (
                    <div className="audio_section">
                        <div>برای ضبط روی شروع کلیک کنید</div>
                        <div style={{ fontSize: 11, display: "flex", justifyContent: "center" }}>حداکثر ۳ دقیقه</div>
                    </div>
                ) : (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        {getTime()}
                        <div className="recording_light"></div>
                    </div>
                )}
            </div>

            <div className="btn_container">
                {recordings ? (
                    <>
                        <button className="button" onClick={() => props.handleAudioUpload(recordings)}>بارگذاری</button>
                        <button className="button error" onClick={handelReset}>دوباره</button>
                    </>
                ) : (
                    <button className="button" disabled={isLoading} onClick={handleRecordButton}>
                        {isRecording ? "توقف" : "شروع"}
                    </button>
                )}
            </div>

            {props.uploading && (
                <div style={{ width: "100%" }}>
                    <LinearProgress
                        variant="determinate"
                        value={props.uploadProcess}
                        color="primary"
                    />
                    <p style={{ textAlign: "center", marginTop: "10px" }}>
                        درحال بارگذاری...
                    </p>
                </div>
            )}
        </div>
    );
}

export default VmsgRecorder;